// Line-count audit (2026-05-04): 66 lines.
import { useCallback } from 'react';
import { usePreBackendCommands } from './usePreBackendCommands';
import { usePreBackendIntentRouting } from './usePreBackendIntentRouting';
import type {
  UsePreBackendRoutingDeps,
  UsePreBackendRoutingReturn,
} from './agentMessagingTypes';

export function usePreBackendRouting(
  deps: UsePreBackendRoutingDeps
): UsePreBackendRoutingReturn {
  const { handleLocalPreBackendCommands } = usePreBackendCommands({
    component: deps.component,
    multipleComponents: deps.multipleComponents,
    setMessages: deps.setMessages,
    setInputValue: deps.setInputValue,
    setIsLoading: deps.setIsLoading,
    persistMessage: deps.persistMessage,
    generateMessageId: deps.generateMessageId,
    addToHistory: deps.addToHistory,
    applyPropertyChanges: deps.applyPropertyChanges,
    updateComponent: deps.updateComponent,
    commandPermissionGuard: deps.commandPermissionGuard,
  });

  const { handleIntentRoutedPreBackendMessage } = usePreBackendIntentRouting({
    component: deps.component,
    multipleComponents: deps.multipleComponents,
    isCanvas: deps.isCanvas,
    agentStatus: deps.agentStatus,
    project: deps.project,
    storePages: deps.storePages,
    setMessages: deps.setMessages,
    setInputValue: deps.setInputValue,
    setIsLoading: deps.setIsLoading,
    setCurrentGenerationMessageId: deps.setCurrentGenerationMessageId,
    setPendingClarification: deps.setPendingClarification,
    persistMessage: deps.persistMessage,
    generateMessageId: deps.generateMessageId,
    generatePageFromDescription: deps.generatePageFromDescription,
    isSlashCommand: deps.isSlashCommand,
    executeSlashCommand: deps.executeSlashCommand,
    applyPropertyChanges: deps.applyPropertyChanges,
    routeToAgent: deps.routeToAgent,
    commandPermissionGuard: deps.commandPermissionGuard,
  });

  const handlePreBackendMessage = useCallback(
    async (userInput: string) => {
      const localCommandResult = await handleLocalPreBackendCommands(userInput);
      if (localCommandResult.handled) {
        return localCommandResult;
      }

      return handleIntentRoutedPreBackendMessage(userInput);
    },
    [handleIntentRoutedPreBackendMessage, handleLocalPreBackendCommands]
  );

  return {
    handlePreBackendMessage,
    handleLocalPreBackendCommands,
    handleIntentRoutedPreBackendMessage,
  };
}
